import React, { useState, useEffect, useCallback } from 'react';
import SelectBuscable from '../components/SelectBuscable';
import { useCompany } from '../CompanyContext';
import { useImpersonation } from '../ImpersonationContext';
import { hasPlanAccess, PLAN_FEATURES } from '../lib/pricing';
import UpgradeWall from '../components/UpgradeWall';
import {
  TasaMetabolica,
  calcularWBGTInterior,
  calcularWBGTExterior,
  calcularTasaMetabolicaPromedio,
  determinarCategoriaPorM,
  verificarCumplimientoWBGT,
} from '../lib/estres-termico';
import {
  createHeatStressCalculation,
  fetchHeatStressCalculations,
  HeatStressCalculationRow,
  HeatStressCalculationInputs,
  HeatStressCalculationResultado,
} from '../lib/gestion-tecnica-estres-termico';
import { generateHeatStressReportPDF } from '../lib/gestion-tecnica-estres-termico-pdf';
import { fetchJobPositionsFull, JobPositionLegalFields } from '../lib/gestion-tecnica-constructor';

// Mismos estilos/paleta que RuidoMethodologyPage.tsx (fondo oscuro, verde
// para CUMPLE, ámbar para advertencias, números principales en 34px).
const inputStyle: React.CSSProperties = {
  width: '100%', background: '#0f172a', border: '1px solid #334155',
  borderRadius: 6, color: '#e2e8f0', padding: '8px 10px', marginTop: 4,
};

const labelStyle: React.CSSProperties = {
  display: 'block', marginBottom: 4, color: '#e2e8f0', fontWeight: 600, fontSize: 13,
};

const smallLabelStyle: React.CSSProperties = {
  display: 'block', marginBottom: 2, color: '#94a3b8', fontSize: 11,
};

const cardStyle: React.CSSProperties = {
  background: '#0f172a', border: '1px solid #334155', borderRadius: 10, padding: 16, marginBottom: 20,
};

const TASA_METABOLICA_LABEL: Record<TasaMetabolica, string> = {
  reposo: 'Reposo',
  ligera: 'Ligera',
  moderada: 'Moderada',
  pesada: 'Pesada',
  muy_pesada: 'Muy pesada',
};

// ResultBadge no está exportado desde RuidoMethodologyPage.tsx (es
// módulo-privado ahí) — se replica aquí tal cual, mismo patrón que el resto
// del codebase (sin componente compartido entre metodologías).
function ResultBadge({ cumple, textoLimite, textoPendiente }: { cumple: boolean; textoLimite: string; textoPendiente: string }) {
  return (
    <div>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, marginBottom: 4,
          background: cumple ? 'rgba(47,191,113,0.12)' : 'rgba(239,68,68,0.12)',
          border: `1px solid ${cumple ? 'rgba(47,191,113,0.3)' : 'rgba(239,68,68,0.3)'}`,
        }}
      >
        <strong style={{ color: cumple ? '#2FBF71' : '#ef4444', fontSize: 14 }}>
          {cumple ? 'CUMPLE' : 'NO CUMPLE'}
        </strong>
        <span style={{ color: '#94a3b8', fontSize: 12 }}>— {textoLimite}</span>
      </div>
      <span style={{ color: '#64748b', fontSize: 11 }}>{textoPendiente}</span>
    </div>
  );
}

export default function EstresTermicoMethodologyPage() {
  const { company, profile } = useCompany();
  const { impersonated }     = useImpersonation();
  const displayCompany       = impersonated ?? company;
  const companyId            = displayCompany?.id ?? '';
  const isSuperAdmin         = profile?.role === 'superadmin';

  // ── Plan gate (after all hooks, mismo criterio que Ruido) ──
  const planAllowed = isSuperAdmin || !displayCompany || hasPlanAccess(displayCompany.plan, 'metodologia-estres-termico');

  const [jobPositions, setJobPositions] = useState<JobPositionLegalFields[]>([]);
  const [savedCalculations, setSavedCalculations] = useState<HeatStressCalculationRow[]>([]);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [descripcion, setDescripcion] = useState('');
  const [selectedJobPositionId, setSelectedJobPositionId] = useState('');

  const [ambiente, setAmbiente] = useState<'interior' | 'exterior'>('interior');
  // Valores de arranque razonables, NO un caso de referencia verificado
  // (a diferencia de LF_EJEMPLO/MF_EJEMPLO en Ruido, que citan un ejemplo
  // resuelto de la NTP con resultado conocido).
  const [tg, setTg] = useState(30);
  const [thn, setThn] = useState(27);
  const [ta, setTa] = useState(30);
  const [tasaTrabajo, setTasaTrabajo] = useState<TasaMetabolica>('moderada');
  const [porcentajeTiempoTrabajado, setPorcentajeTiempoTrabajado] = useState(100);
  const [aclimatado, setAclimatado] = useState(true);

  const [guardando, setGuardando] = useState(false);
  const [guardadoOk, setGuardadoOk] = useState(false);
  const [ultimoCalculoGuardado, setUltimoCalculoGuardado] = useState<HeatStressCalculationRow | null>(null);
  const [descargandoPdf, setDescargandoPdf] = useState(false);

  const loadData = useCallback(async () => {
    if (!companyId) { setLoadingData(false); return; }
    setLoadingData(true);
    try {
      const [positions, calculations] = await Promise.all([
        fetchJobPositionsFull(companyId),
        fetchHeatStressCalculations(companyId),
      ]);
      setJobPositions(positions);
      setSavedCalculations(calculations);
    } catch (e: any) {
      setError(e.message ?? 'Error al cargar datos');
    } finally {
      setLoadingData(false);
    }
  }, [companyId]);

  useEffect(() => { loadData(); }, [loadData]);

  if (!planAllowed) {
    return <UpgradeWall {...PLAN_FEATURES['metodologia-estres-termico']} />;
  }

  if (loadingData) return <div style={{ padding: 24, color: '#94a3b8' }}>Cargando…</div>;

  // ===== Cálculo — índice WBGT =====
  const wbgt = ambiente === 'interior' ? calcularWBGTInterior(tg, thn) : calcularWBGTExterior(tg, thn, ta);
  const mPromedio = calcularTasaMetabolicaPromedio(tasaTrabajo, porcentajeTiempoTrabajado);
  const categoria = determinarCategoriaPorM(mPromedio);
  const { cumple, limite } = verificarCumplimientoWBGT(wbgt, categoria, aclimatado);

  const puedeGuardar = descripcion.trim().length > 0;

  const handleGuardar = async () => {
    setError(null);
    if (!descripcion.trim()) { setError('Ingresa una descripción del local/puesto/tarea evaluada'); return; }

    setGuardando(true);
    setGuardadoOk(false);
    try {
      const inputs: HeatStressCalculationInputs = {
        tg, thn, ta: ambiente === 'exterior' ? ta : undefined,
        tasaTrabajo, porcentajeTiempoTrabajado, aclimatado, ambiente,
      };
      const resultado: HeatStressCalculationResultado = { wbgt, mPromedio, categoria, limite, cumple };

      await createHeatStressCalculation({
        companyId,
        jobPositionId: selectedJobPositionId || undefined,
        descripcion,
        ambiente,
        inputs,
        resultado,
        cumpleNormativa: cumple,
        createdBy: profile?.id ?? null,
      });
      // createHeatStressCalculation() no retorna el registro insertado
      // (mismo patrón que createNoiseCalculation()) — se vuelve a consultar
      // para obtener el registro recién creado con su id real, necesario
      // para el PDF.
      const calculations = await fetchHeatStressCalculations(companyId);
      setSavedCalculations(calculations);
      setUltimoCalculoGuardado(calculations[0] ?? null);
      setGuardadoOk(true);
    } catch (e: any) {
      setError(e.message ?? 'Error al guardar el cálculo');
    } finally {
      setGuardando(false);
    }
  };

  const handleDescargarPdf = async () => {
    if (!ultimoCalculoGuardado) {
      setError('No hay un cálculo guardado para generar el PDF');
      return;
    }
    setDescargandoPdf(true);
    try {
      await generateHeatStressReportPDF(
        ultimoCalculoGuardado,
        companyId,
        displayCompany?.name ?? 'Empresa',
        profile?.id ?? null
      );
    } catch (e: any) {
      setError(e.message ?? 'Error al generar el PDF');
    } finally {
      setDescargandoPdf(false);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 900 }}>
      <h1 style={{ fontSize: 22, marginBottom: 6, color: '#e2e8f0' }}>Metodología de Estrés Térmico</h1>
      <p style={{ fontSize: 13, color: '#94a3b8', marginBottom: 20 }}>
        Cálculo del índice WBGT según NTP 1189 (INSST) y norma UNE-EN ISO 7243:2017.
      </p>

      {error && (
        <div style={{ background: '#7f1d1d', color: '#fecaca', padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 }}>
          {error}
        </div>
      )}

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Descripción (local, puesto o tarea evaluada)</label>
        <input
          style={inputStyle}
          type="text"
          value={descripcion}
          onChange={e => setDescripcion(e.target.value)}
          placeholder="Ej. Horno de fundición, Cuadrilla de vías al aire libre..."
        />
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle}>Puesto de trabajo (opcional)</label>
        <SelectBuscable inputStyle={{ ...inputStyle, paddingRight: 32 }} ariaLabel="Puesto de trabajo (opcional)" value={selectedJobPositionId}
          onChange={setSelectedJobPositionId} vacio="— Sin asociar —" opciones={jobPositions.map(p => ({ value: p.id, label: p.name }))} />
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
        <button
          onClick={() => setAmbiente('interior')}
          style={{
            background: ambiente === 'interior' ? '#334155' : 'transparent', color: '#e2e8f0', border: '1px solid #334155',
            borderRadius: 6, padding: '8px 16px', fontSize: 13, cursor: 'pointer',
          }}
        >
          Interior
        </button>
        <button
          onClick={() => setAmbiente('exterior')}
          style={{
            background: ambiente === 'exterior' ? '#334155' : 'transparent', color: '#e2e8f0', border: '1px solid #334155',
            borderRadius: 6, padding: '8px 16px', fontSize: 13, cursor: 'pointer',
          }}
        >
          Exterior
        </button>
      </div>

      <div style={cardStyle}>
        <h2 style={{ fontSize: 14, color: '#e2e8f0', marginTop: 0, marginBottom: 12 }}>Temperaturas medidas</h2>

        <div style={{ display: 'grid', gridTemplateColumns: ambiente === 'exterior' ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)', gap: 12, marginBottom: 4 }}>
          <div>
            <label style={smallLabelStyle}>Temperatura de globo (tg, °C)</label>
            <input style={inputStyle} type="number" value={tg} onChange={e => setTg(parseFloat(e.target.value) || 0)} />
          </div>
          <div>
            <label style={smallLabelStyle}>Temperatura húmeda natural (thn, °C)</label>
            <input style={inputStyle} type="number" value={thn} onChange={e => setThn(parseFloat(e.target.value) || 0)} />
          </div>
          {ambiente === 'exterior' && (
            <div>
              <label style={smallLabelStyle}>Temperatura del aire (ta, °C)</label>
              <input style={inputStyle} type="number" value={ta} onChange={e => setTa(parseFloat(e.target.value) || 0)} />
            </div>
          )}
        </div>
      </div>

      <div style={cardStyle}>
        <h2 style={{ fontSize: 14, color: '#e2e8f0', marginTop: 0, marginBottom: 12 }}>Carga de trabajo</h2>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Tasa metabólica de la tarea</label>
          <select style={{ ...inputStyle, maxWidth: 240 }} value={tasaTrabajo} onChange={e => setTasaTrabajo(e.target.value as TasaMetabolica)}>
            {(Object.keys(TASA_METABOLICA_LABEL) as TasaMetabolica[]).map(t => (
              <option key={t} value={t} style={{ color: '#000000' }}>{TASA_METABOLICA_LABEL[t]}</option>
            ))}
          </select>
        </div>

        <div style={{ marginBottom: 14 }}>
          <label style={labelStyle}>Porcentaje de tiempo trabajado (vs. reposo)</label>
          <input
            style={{ ...inputStyle, maxWidth: 160 }}
            type="number" min={0} max={100}
            value={porcentajeTiempoTrabajado}
            onChange={e => setPorcentajeTiempoTrabajado(Math.min(100, Math.max(0, parseFloat(e.target.value) || 0)))}
          />
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input type="checkbox" checked={aclimatado} onChange={e => setAclimatado(e.target.checked)} />
          <span style={{ color: '#e2e8f0', fontSize: 13 }}>Trabajador aclimatado al calor</span>
        </label>
      </div>

      <div style={cardStyle}>
        <ResultBadge
          cumple={cumple}
          textoLimite={`WBGT: ${wbgt.toFixed(1)} °C — límite ${limite} °C (${TASA_METABOLICA_LABEL[categoria]}, ${aclimatado ? 'aclimatado' : 'no aclimatado'})`}
          textoPendiente="Método de cribado (NTP 1189/ISO 7243) — ante un resultado límite, corresponde un análisis más detallado (PHS/ISO 7933)."
        />

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 14, fontSize: 13, color: '#e2e8f0' }}>
          <div>
            <span style={{ color: '#94a3b8' }}>WBGT calculado</span>
            <div style={{ fontSize: 34, fontWeight: 600 }}>{wbgt.toFixed(1)} °C</div>
          </div>
          <div>
            <span style={{ color: '#94a3b8' }}>Tasa metabólica promedio (M)</span>
            <div style={{ fontSize: 34, fontWeight: 600 }}>{mPromedio.toFixed(0)} W/m²</div>
          </div>
        </div>
        <span style={{ color: '#64748b', fontSize: 11, display: 'block', marginTop: 8 }}>
          Categoría resultante: {TASA_METABOLICA_LABEL[categoria]}
        </span>
      </div>

      <button
        onClick={handleGuardar}
        disabled={guardando || !puedeGuardar}
        style={{
          background: '#2FBF71', color: '#0f172a', border: 'none', borderRadius: 6,
          padding: '8px 16px', fontWeight: 600, cursor: guardando || !puedeGuardar ? 'not-allowed' : 'pointer',
          opacity: guardando || !puedeGuardar ? 0.6 : 1,
        }}
      >
        {guardando ? 'Guardando...' : 'Guardar cálculo'}
      </button>

      {guardadoOk && (
        <div style={{ color: '#2FBF71', fontSize: 13, marginTop: 10 }}>✓ Cálculo guardado correctamente</div>
      )}

      {guardadoOk && ultimoCalculoGuardado && (
        <button
          onClick={handleDescargarPdf}
          disabled={descargandoPdf}
          style={{
            background: '#3b82f6', color: '#0f172a', border: 'none', borderRadius: 6,
            padding: '8px 16px', fontWeight: 600, cursor: descargandoPdf ? 'not-allowed' : 'pointer',
            opacity: descargandoPdf ? 0.6 : 1, marginTop: 10, marginLeft: 10,
          }}
        >
          {descargandoPdf ? 'Generando PDF...' : 'Descargar reporte PDF'}
        </button>
      )}

      {savedCalculations.length > 0 && (
        <div style={{ marginTop: 24 }}>
          <h2 style={{ fontSize: 15, color: '#e2e8f0', marginBottom: 8 }}>Cálculos guardados</h2>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, color: '#e2e8f0' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid #334155', textAlign: 'left' }}>
                <th style={{ padding: '6px 8px' }}>Descripción</th>
                <th style={{ padding: '6px 8px' }}>Ambiente</th>
                <th style={{ padding: '6px 8px' }}>WBGT</th>
                <th style={{ padding: '6px 8px' }}>Cumple</th>
                <th style={{ padding: '6px 8px' }}>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {savedCalculations.map(c => (
                <tr key={c.id} style={{ borderBottom: '1px solid #1e293b' }}>
                  <td style={{ padding: '6px 8px' }}>{c.descripcion}</td>
                  <td style={{ padding: '6px 8px' }}>{c.ambiente}</td>
                  <td style={{ padding: '6px 8px' }}>{c.resultado.wbgt.toFixed(1)} °C</td>
                  <td style={{ padding: '6px 8px' }}>{c.cumple_normativa ? '✓' : '✗'}</td>
                  <td style={{ padding: '6px 8px' }}>{new Date(c.created_at).toLocaleDateString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
